import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';

import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import LinkField from '../../ProductMetafields/fields/LinkField';
import MediaField from '../../ProductMetafields/fields/MediaField';
import { aggregatedFieldStatePropType, aggregatedFieldStatsPropType } from '../helpers/aggregatedFieldPropTypes';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import AggregatedBoundScalarField from './AggregatedBoundScalarField';

const MEDIA_TYPES = new Set(['file', 'image', 'video']);

const AggregatedCustomAttributeField = ({
  item,
  field,
  readOnly,
  readOnlyTooltip,
}) => {
  const fieldKey = item.title;
  const bindings = useBindAggregatedField(fieldKey, field, item.title, { isMeta: true });
  const isCommon = field?.state === AGGREGATED_FIELD_STATE.COMMON;
  const structuredValue = isCommon ? field.value : '';

  const handleStructuredChange = useCallback((nextValue, _data, onCancel) => {
    bindings.onChange(nextValue, onCancel);
  }, [bindings]);

  if (MEDIA_TYPES.has(item.type)) {
    return (
      <MediaField
        data={item}
        value={structuredValue}
        type={item.type}
        disabled
        onChange={undefined}
      />
    );
  }

  if (item.type === 'link') {
    return (
      <LinkField
        data={item}
        value={structuredValue}
        disabled={readOnly}
        onChange={handleStructuredChange}
      />
    );
  }

  return (
    <AggregatedBoundScalarField
      fieldKey={fieldKey}
      field={field}
      label={item.title}
      readOnly={readOnly}
      readOnlyTooltip={readOnlyTooltip}
      isMeta
    />
  );
};

AggregatedCustomAttributeField.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
  }).isRequired,
  field: PropTypes.shape({
    state: aggregatedFieldStatePropType,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.shape({
        name: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ]),
    stats: aggregatedFieldStatsPropType,
  }),
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
};

AggregatedCustomAttributeField.defaultProps = {
  field: null,
  readOnly: false,
  readOnlyTooltip: null,
};

export default memo(AggregatedCustomAttributeField);
